import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';
import { faArrowUp, faMicrophone } from '@fortawesome/pro-regular-svg-icons';
import { faStop } from '@fortawesome/pro-solid-svg-icons';
import { Button } from 'components/ui/button';
import { Textarea } from '@buildoutinc/blueprint-react/ui/Textarea';
import { AttachmentBadge } from './attachments/badge';
import { AttachmentsInput } from './attachments/input';

const DEFAULT_PLACEHOLDER = 'Ask Otto for assistance';

// Stays open during a turn, and submitting interrupts it; a surface whose turns cannot be
// interrupted passes `disabled` instead.
interface Props {
  ariaLabel?: string;
  attachments?: File[];
  disabled?: boolean;
  input: string;
  placeholder?: string;
  recording?: boolean;
  sttSupported?: boolean;
  onAddAttachments?: (attachments: File[]) => void;
  onInputChange: (value: string) => void;
  onMicClick?: () => void;
  onRemoveAttachment?: (index: number) => void;
  onSubmit: () => void;
}

export default function MessageInput({
  ariaLabel,
  attachments = [],
  disabled = false,
  input,
  placeholder = DEFAULT_PLACEHOLDER,
  recording = false,
  sttSupported = false,
  onAddAttachments,
  onInputChange,
  onMicClick,
  onRemoveAttachment,
  onSubmit,
}: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const hasValue = input.trim().length > 0;

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    el.style.height = 'auto';

    // A textarea inside a collapsed surface reports scrollHeight 0, and this won't re-run when the
    // surface opens - writing that would leave the box flattened until the next keystroke.
    if (el.scrollHeight) {
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [input]);

  // Enter sends; Shift+Enter inserts a newline
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div
      className={classNames('ai-assistant-composer d-flex flex-column gap-3 p-3', {
        'ai-assistant-composer--expanded': hasValue,
      })}
    >
      {attachments.length > 0 && (
        <div className="d-flex flex-wrap gap-2">
          {attachments.map((attachment, index) => (
            <AttachmentBadge
              key={index}
              name={attachment.name}
              size={attachment.size}
              onRemove={onRemoveAttachment && (() => onRemoveAttachment(index))}
            />
          ))}
        </div>
      )}

      <div className="d-flex flex-wrap align-items-center gap-3">
        {onAddAttachments && <AttachmentsInput disabled={disabled} onAddAttachments={onAddAttachments} />}
        <Textarea
          aria-label={ariaLabel}
          className="ai-assistant-composer-input"
          disabled={disabled}
          placeholder={recording ? 'Listening…' : placeholder}
          ref={inputRef}
          rows={1}
          value={input}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onInputChange(e.target.value)}
          onKeyDown={handleInputKeyDown}
        />
        <div className="d-flex align-items-center gap-2 ms-auto">
          {sttSupported && onMicClick && (
            <Button
              appearance="muted"
              aria-label={recording ? 'Stop recording' : 'Speak to Otto'}
              className={classNames({ 'text-danger': recording })}
              disabled={disabled}
              icon={recording ? faStop : faMicrophone}
              size="sm"
              variant="ghost"
              onClick={onMicClick}
            />
          )}
          {(hasValue || attachments.length > 0) && (
            <Button
              aria-label="Send message"
              className="ai-assistant-composer-send"
              disabled={disabled || !hasValue}
              icon={faArrowUp}
              size="sm"
              variant="primary"
              onClick={onSubmit}
            />
          )}
        </div>
      </div>
    </div>
  );
}
